import { useLocation } from 'react-router-dom'

import { Box, CounterBadge, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import ScrollableList from 'modules/core/components/ScrollableList'
import createComputed from 'modules/core/hooks/createComputed'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI } from 'modules/core/lib/dates'

import type { AnalyticResults } from 'modules/enps/store/collections/analyticResults'
import type { Cycle } from 'modules/enps/store/models/AnalyticResult'

const useCyclesOptions = createComputed((analyticResults: AnalyticResults) => {
  return Array.from(analyticResults.cycles).map(([startDate, endDate]) => ({
    label: `${dateBackendToUI(startDate)} - ${dateBackendToUI(endDate)}`,
    value: [startDate, endDate].join(' - '),
  }))
})

interface Props {
  analyticResults: AnalyticResults
  cycle: Cycle | null
  setCycle: (cycle: Cycle) => void
}

const CycleSelector = ({ analyticResults, setCycle, cycle }: Props) => {
  const { pathname } = useLocation()
  const cyclesOptions = useCyclesOptions(analyticResults).get()
  const [open, toggle] = useToggle(false)
  const isDisabled = cyclesOptions.length <= 1

  const onSelectCycle = (cycle: string | null) => {
    setCycle((cycle?.split(' - ') as Cycle) || null)
    const path = (pathname.split('/').pop() + 'CyclesFilter') as keyof typeof trackingEvents.enps
    track(trackingEvents.enps[path])
  }

  const selectedCycle = (cycle: Cycle | null) => {
    const localeData = moment.localeData()
    const timeFormat = localeData.longDateFormat('L')
    const startDate = cycle && moment(cycle[0]).format(timeFormat)
    const endDate = cycle && moment(cycle[1]).format(timeFormat)

    return `${startDate} - ${endDate}`
  }

  return (
    <>
      {!!cyclesOptions.length && (
        <PopoverMenu type="list" onClick={toggle(!open)} open={open}>
          <PopoverMenu.Trigger disabled={!!isDisabled}>
            <Box
              flexDirection="row"
              alignItems="center"
              width="s300"
              justifyContent="spaceBetween"
              background={isDisabled ? 'grey200' : 'grey000White'}
              border={{
                all: {
                  width: 's2',
                  style: 'solid',
                  color: open ? 'grey600' : 'grey400',
                },
              }}
              borderRadius={{
                all: 'abs008',
              }}
              padding="s8"
              cursor="pointer"
            >
              <Box flexDirection="row" gap="s6">
                <CounterBadge count={0} noCountIcon="calendar" />
                <Text size="300" color="grey1000">
                  {selectedCycle(cycle)}
                </Text>
              </Box>
              <Icon.Small icon={open ? 'caret-up' : 'caret-down'} />
            </Box>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center" width="s300">
            <ScrollableList>
              {cyclesOptions.map(({ value, label }) => (
                <Box key={value} alignItems="center" width="full">
                  <PopoverMenu.TouchableOption
                    key={value}
                    title={label}
                    onClick={() => onSelectCycle(value)}
                    fullWidth
                  />
                </Box>
              ))}
            </ScrollableList>
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
    </>
  )
}

export default CycleSelector
